import React, { useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { RefreshCw, Download, Info, Save } from 'lucide-react';

// RISE with SAP "Full Use Equivalent" conversion simulator.
// Data source: GET /api/sap/roles/fue-sim (backend sapFueSim.js).
const CATS = [
  { key: 'advanced', label: 'Advanced Use' },
  { key: 'core', label: 'Core Use' },
  { key: 'selfService', label: 'Self-Service Use' }
];

export default function FuePanel({ connId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [detQ, setDetQ] = useState('');
  const [detCat, setDetCat] = useState('');

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const res = await api.get('/api/sap/roles/fue-sim', { params: { connectionId: connId } });
      setData(res.data);
      setForm({
        advanced: res.data.config.ratios.advanced,
        core: res.data.config.ratios.core,
        selfService: res.data.config.ratios.selfService,
        minRoles: res.data.config.scopeUp.minRoles,
        minModules: res.data.config.scopeUp.minModules
      });
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Failed to load the FUE simulation.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [connId]);

  const save = async () => {
    setMsg(null);
    try {
      await api.put('/api/sap/roles/fue-config', {
        ratios: { advanced: Number(form.advanced), core: Number(form.core), selfService: Number(form.selfService) },
        scopeUp: { minRoles: Number(form.minRoles), minModules: Number(form.minModules) }
      });
      setMsg('Saved. Recalculating...');
      await load();
      setMsg('Saved.');
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Failed to save the configuration.');
    }
  };

  const exportCsv = () => {
    if (!data) return;
    const head = ['Scenario', 'Advanced users', 'Core users', 'Self-Service users',
      'FUE advanced', 'FUE core', 'FUE self-service', 'FUE total', 'FUE billable', 'Excluded users'];
    const esc = v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
    const body = data.scenarios.map(s => [s.label, s.counts.advanced, s.counts.core, s.counts.selfService,
      s.fueByCategory.advanced, s.fueByCategory.core, s.fueByCategory.selfService,
      s.fueRaw, s.fueBillable, s.excludedCount]);
    const blob = new Blob([[head.map(esc).join(','), ...body.map(b => b.map(esc).join(','))].join('\r\n')],
      { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'fue_simulation_' + connId + '.csv';
    a.click();
  };

  const Num = ({ label, value, onChange }) => (
    <label className="flex items-center gap-2 text-sm text-slate-600">
      {label}
      <input type="number" min="1" value={value} onChange={e => onChange(e.target.value)}
        className="w-20 px-2 py-1 rounded border border-slate-200 text-sm" />
    </label>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={load} disabled={loading}
          className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
        <button onClick={exportCsv} disabled={!data}
          className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5">
          <Download size={14} /> Export CSV
        </button>
        <span className="text-sm text-slate-500 ml-auto">
          {data ? data.usersAnalysed + ' active users converted' : ''}
        </span>
      </div>

      {err && <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm px-4 py-3">{err}</div>}
      {msg && <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm px-4 py-3">{msg}</div>}

      {form && (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 flex flex-wrap items-center gap-4">
          <span className="text-xs uppercase tracking-wide text-slate-400">Conversion ratios (users per 1 FUE)</span>
          <Num label="Advanced" value={form.advanced} onChange={v => setForm({ ...form, advanced: v })} />
          <Num label="Core" value={form.core} onChange={v => setForm({ ...form, core: v })} />
          <Num label="Self-Service" value={form.selfService} onChange={v => setForm({ ...form, selfService: v })} />
          <span className="text-xs uppercase tracking-wide text-slate-400 ml-2">Wide-scope threshold</span>
          <Num label="min roles" value={form.minRoles} onChange={v => setForm({ ...form, minRoles: v })} />
          <Num label="min modules" value={form.minModules} onChange={v => setForm({ ...form, minModules: v })} />
          <button onClick={save}
            className="ml-auto px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm inline-flex items-center gap-1.5">
            <Save size={14} /> Save &amp; recalculate
          </button>
        </div>
      )}

      {data && (
        <details className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">
          <summary className="cursor-pointer text-sm font-semibold text-slate-800">How these numbers are built</summary>
          <div className="mt-3 space-y-2">
            <p><b>Formula.</b> FUE = Advanced users / {data.config.ratios.advanced} + Core users / {data.config.ratios.core} + Self-Service users / {data.config.ratios.selfService}. The billable figure is that total rounded up.</p>
            <p><b>Population.</b> {data.usersAnalysed} active users from the SAP User Maintenance snapshot (USR06 licence class + USR02 validity). Locked and expired users are out of scope.</p>
            <p><b>Step 1, licence mapping.</b> {Object.keys(data.config.map).map(k => k + ' → ' + data.config.map[k]).join(', ')}. Licences {(data.config.excludedLicenses || []).map(x => x || 'blank').join(' and ')} are not counted.</p>
            <p><b>Step 2, reclassification.</b> Scenarios B onward move users the audit flagged: under-licensed → {data.config.reclass.underLicensedTo}, over-licensed → {data.config.reclass.overLicensedTo}. Scenario C also promotes wide-scope users ({data.config.scopeUp.minRoles}+ roles or {data.config.scopeUp.minModules}+ modules) to {data.config.scopeUp.toCategory}. Scenario D drops dormant users.</p>
            <p><b>Scenario E, calibrated.</b> Fitted against the official SAP FUE list for Q01. A user counts as Advanced Use when they hold any of these transactions: <span className="font-mono">{(data.config.advancedTriggers || []).join(', ')}</span>. On Q01 this matched 85 of the 88 users SAP classified as Advanced, with no false positives; the 3 misses were users with no roles at all plus one locked account. It has <b>not</b> been validated against a production FUE list yet.</p>
            <p><b>Sources.</b> sap_um_users (USR06/USR02), sap_rm_role_users (AGR_USERS), sap_rm_role_tcodes (AGR_1251). Everything is read from the dashboard cache, so click Load Roles first for fresh data. Ratios and triggers live in sap_rm_fue_config and can be edited above.</p>
            <p className="text-slate-400">Click any scenario card to see, user by user, which category it landed in and why.</p>
          </div>
        </details>
      )}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {data.scenarios.map(s => (
            <div key={s.key} onClick={() => setOpenKey(openKey === s.key ? null : s.key)}
              className={'rounded-lg border bg-white p-4 cursor-pointer transition ' + (openKey === s.key ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300')}>
              <div className="text-sm font-semibold text-slate-800">{s.label}</div>
              <div className="text-xs text-slate-500 mt-1 min-h-[32px]">{s.description}</div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-3xl font-semibold text-slate-800">{s.fueBillable}</span>
                <span className="text-xs text-slate-500">FUE billable</span>
              </div>
              <div className="text-xs text-slate-400">exact {s.fueRaw} &middot; {openKey === s.key ? 'click to close' : 'click for detail'}</div>
              <table className="w-full text-xs mt-3">
                <tbody>
                  {CATS.map(c => (
                    <tr key={c.key} className="border-t border-slate-100">
                      <td className="py-1 text-slate-500">{c.label}</td>
                      <td className="py-1 text-right tabular-nums text-slate-700">{s.counts[c.key]}</td>
                      <td className="py-1 text-right tabular-nums text-slate-400">{s.fueByCategory[c.key]} FUE</td>
                    </tr>
                  ))}
                  <tr className="border-t border-slate-200">
                    <td className="py-1 text-slate-400">Not counted</td>
                    <td className="py-1 text-right tabular-nums text-slate-400" colSpan={2}>{s.excludedCount}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {data && openKey && (() => {
        const sc = data.scenarios.find(x => x.key === openKey);
        const term = detQ.trim().toUpperCase();
        const detailRows = data.users.filter(u => {
          const d = u.byScenario[openKey] || {};
          const catOk = detCat === '' || (detCat === 'none' ? !d.cat : d.cat === detCat);
          const qOk = term === '' || u.username.includes(term) ||
            (u.company || '').toUpperCase().includes(term) ||
            (u.department || '').toUpperCase().includes(term) ||
            (d.reason || '').toUpperCase().includes(term);
          return catOk && qOk;
        });
        const dlCsv = () => {
          const head = ['User', 'Full name', 'Company', 'Dept', 'Licence', 'Licence text', 'Roles', 'Modules', 'Idle (days)', 'FUE category', 'Why'];
          const esc = v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
          const body = detailRows.map(u => { const d = u.byScenario[openKey] || {}; return [u.username, u.fullName, u.company, u.department, u.license, u.licenseText, u.roles, (u.modules || []).join(' '), u.idleDays == null ? '' : u.idleDays, d.cat || 'not counted', d.reason || '']; });
          const blob = new Blob([[head.map(esc).join(','), ...body.map(b => b.map(esc).join(','))].join('\r\n')], { type: 'text/csv;charset=utf-8;' });
          const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
          a.download = 'fue_detail_' + openKey + '_' + connId + '.csv'; a.click();
        };
        return (
          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-slate-800">{sc.label} &mdash; how each user was counted</span>
              <select value={detCat} onChange={e => setDetCat(e.target.value)}
                className="px-2 py-1 rounded border border-slate-200 text-xs">
                <option value="">All categories</option>
                <option value="advanced">Advanced Use ({sc.counts.advanced})</option>
                <option value="core">Core Use ({sc.counts.core})</option>
                <option value="selfService">Self-Service Use ({sc.counts.selfService})</option>
                <option value="none">Not counted ({sc.excludedCount})</option>
              </select>
              <input value={detQ} onChange={e => setDetQ(e.target.value)} placeholder="Search user / dept / reason..."
                className="px-2 py-1 rounded border border-slate-200 text-xs flex-1 min-w-[180px]" />
              <button onClick={dlCsv} className="px-2 py-1 rounded border border-slate-200 text-xs text-slate-600 hover:bg-slate-50">Export detail CSV</button>
              <span className="text-xs text-slate-500">{detailRows.length} rows</span>
            </div>
            <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">User</th>
                    <th className="text-left px-3 py-2">Company / Dept</th>
                    <th className="text-left px-3 py-2">Licence</th>
                    <th className="text-right px-3 py-2">Roles</th>
                    <th className="text-left px-3 py-2">Modules</th>
                    <th className="text-left px-3 py-2">Counted as</th>
                    <th className="text-left px-3 py-2">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {detailRows.map(u => { const d = u.byScenario[openKey] || {};
                    return (
                      <tr key={u.username} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-800">{u.username}
                          <div className="text-slate-400">{u.fullName}</div></td>
                        <td className="px-3 py-2 text-slate-600">{u.company} {u.department ? '/ ' + u.department : ''}</td>
                        <td className="px-3 py-2 text-slate-600">{u.license || '-'}
                          <div className="text-slate-400">{u.licenseText}</div></td>
                        <td className="px-3 py-2 text-right tabular-nums">{u.roles}</td>
                        <td className="px-3 py-2 text-slate-500">{(u.modules || []).join(' · ') || '-'}</td>
                        <td className="px-3 py-2">
                          <span className={'px-2 py-0.5 rounded border ' + (d.cat === 'advanced' ? 'bg-rose-50 text-rose-700 border-rose-200' : d.cat === 'core' ? 'bg-amber-50 text-amber-700 border-amber-200' : d.cat === 'selfService' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200')}>
                            {d.cat === 'selfService' ? 'Self-Service' : d.cat === 'advanced' ? 'Advanced' : d.cat === 'core' ? 'Core' : 'Not counted'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-600">{d.reason}</td>
                      </tr>);
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
      {data && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs px-4 py-3 flex gap-2">
          <Info size={14} className="mt-0.5 shrink-0" />
          <ul className="list-disc pl-4 space-y-1">
            {data.caveats.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
